<template>
    <!-- Overlay -->
    <div v-if="isVisible" class="feedback-overlay" @click="closeModal"></div>

    <!-- Modal -->
    <transition name="slide-up">
    <div v-if="isVisible" class="feedback-modal">
            <!-- Close button -->
            <button
                type="button"
                class="feedback-close-btn icon-btn icon-btn-white"
                @click="closeModal"
            >
                <i class="fi fi-x"></i>
            </button>

            <div class="feedback-modal-content">
                <!-- Icon -->
                <img
                    v-if="!imageLoadError"
                    src="@/assets/images/icons/feedback-icon.svg"
                    alt="Feedback icon - Share your experience with the IPAC Dashboard"
                    class="feedback-icon mb-3"
                    @error="handleImageError"
                />

                <!-- Title - Fixed -->
                <h2 class="feedback-title mb-3">
                    Please tell us about your experience with the IPAC
                    Dashboard.
                </h2>

                <!-- Form -->
                <form @submit.prevent="submitForm" class="feedback-form">
                    <!-- Scrollable Form Content -->
                    <div class="feedback-form-scrollable">
                        <!-- Features used most often -->
                        <div class="form-section mb-2">
                            <label class="form-section-label mb-2">
                                Which features do you use most often? (Select
                                all that apply)
                            </label>
                            <CheckboxGroup
                                v-model="formData.featuresUsed"
                                :options="featureOptions"
                                name="features-used"
                            />
                        </div>

                        <!-- Satisfaction ratings -->
                        <div class="form-section mb-2">
                            <label class="form-section-label mb-1">
                                How satisfied are you with the following
                                features?
                            </label>
                            <div class="satisfaction-scale-label mb-2">
                                1 (Very unsatisfied) - 5 (Very satisfied)
                            </div>
                            <div
                                v-for="feature in featureOptions"
                                :key="feature.value"
                                class="satisfaction-item mb-1"
                            >
                                <div
                                    class="satisfaction-row d-flex align-items-center"
                                >
                                    <label class="satisfaction-feature-label">{{
                                        feature.label
                                    }}</label>
                                    <div class="radio-group-wrapper">
                                        <RadioGroup
                                            v-model="
                                                formData.satisfaction[
                                                    feature.value
                                                ]
                                            "
                                            :options="ratingOptions"
                                            :name="`satisfaction-${feature.value}`"
                                            :stacked="false"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Recommendation likelihood -->
                        <div class="form-section mb-2">
                            <label class="form-section-label mb-1">
                                How likely are you to recommend the IPAC
                                Dashboard to a colleague?
                            </label>
                            <div class="satisfaction-scale-label mb-2">
                                1 (Very Unlikely) - 5 (Extremely Likely)
                            </div>
                            <div class="radio-group-wrapper">
                                <RadioGroup
                                    v-model="formData.recommendation"
                                    :options="ratingOptions"
                                    name="recommendation"
                                    :stacked="false"
                                />
                            </div>
                        </div>

                        <!-- Additional feedback textarea -->
                        <div class="form-section mb-3">
                            <Field
                                label="Please share any more feedback"
                                v-model="formData.additionalFeedback"
                                type="textarea"
                                required
                                :params="{
                                    placeholder:
                                        'Please share any feedback you may have. Thank you!',
                                }"
                            />
                        </div>

                        <!-- Contact permission checkbox -->
                        <div class="form-section">
                            <div class="form-check d-flex align-items-center">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    id="contact-permission"
                                    v-model="formData.mayContact"
                                />
                                <label
                                    for="contact-permission"
                                    class="form-check-label"
                                >
                                    May we contact you to learn more about your
                                    feedback?
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Action buttons - Fixed at bottom -->
                    <div class="feedback-actions d-flex gap-2">
                        <button
                            type="button"
                            class="btn btn-white flex-fill"
                            @click="skipFeedback"
                        >
                            Skip for now
                        </button>
                        <button
                            type="submit"
                            class="btn btn-primary flex-fill"
                            :disabled="isSubmitting"
                        >
                            {{ isSubmitting ? 'Submitting...' : 'Submit' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </transition>
</template>

<script>
import CheckboxGroup from '@/components/fields/CheckboxGroup.vue';
import RadioGroup from '@/components/fields/RadioGroup.vue';
import Field from '@/components/Field.vue';
import { shouldShowFeedbackModal, setLastShownDate, setFeedbackSnooze } from '@/utils/feedbackModalUtils';

export default {
    name: 'FeedbackModal',
    components: {
        CheckboxGroup,
        RadioGroup,
        Field,
    },
    data() {
        return {
            isVisible: false,
            featureOptions: [
                { label: 'Auditing Suite', value: 'auditing-suite' },
                { label: 'Education Tracking', value: 'education-tracking' },
                { label: 'Knowledge Library', value: 'knowledge-library' },
                { label: 'Ask Patty AI Search', value: 'ask-patty' },
            ],
            ratingOptions: [
                { label: '1', value: 1 },
                { label: '2', value: 2 },
                { label: '3', value: 3 },
                { label: '4', value: 4 },
                { label: '5', value: 5 },
            ],
            formData: {
                featuresUsed: [],
                satisfaction: {
                    'auditing-suite': null,
                    'education-tracking': null,
                    'knowledge-library': null,
                    'ask-patty': null,
                },
                recommendation: null,
                additionalFeedback: '',
                mayContact: false,
            },
            imageLoadError: false,
            previousBodyOverflow: null,
            isSubmitting: false,
        };
    },
    emits: ['close', 'submit', 'skip'],
    computed: {
        featureValueToLabel() {
            return this.featureOptions.reduce((acc, option) => {
                acc[option.value] = option.label;
                return acc;
            }, {});
        },
        shouldCheckVisibility() {
            // Only show if:
            // 1. Route is not explicitly public (requiresAuth !== false)
            // 2. User is authenticated
            // 3. Route exists (has been resolved by router)
            return (
                this.$route.meta?.requiresAuth !== false &&
                this.$userStore?.isAuthenticated &&
                this.$route &&
                this.$route.matched.length > 0
            );
        },
    },
    methods: {
        /**
         * Check if feedback modal should be shown and display it if conditions are met
         */
        checkAndShowFeedbackModal() {
            // Only check if modal is not already visible
            if (this.isVisible) return;
            
            // Only show in authenticated layout contexts
            if (!this.shouldCheckVisibility) return;
            
            // Check if modal should be shown
            if (shouldShowFeedbackModal(this.$userStore)) {
                this.isVisible = true;
                // Note: We do NOT set last shown date here anymore. 
                // It is set only on successful submission.
                // Snooze is set on skip/close.
            }
        },
        closeModal() {
            this.handleDismissal();
        },
        handleDismissal() {
             const userId = this.$userStore?.user?.id;
            if (userId && this.isVisible) {
                // If closed without submitting, snooze for 30 days
                setFeedbackSnooze(userId, 30);
            }
            this.isVisible = false;
            // Emit close just in case parent needs to know, though not strictly required for visibility anymore
            this.$emit('close');
        },
        handleImageError() {
            // Hide image if it fails to load
            this.imageLoadError = true;
            console.warn('Feedback icon image failed to load');
        },
        async submitForm() {
            // Prevent duplicate submissions
            if (this.isSubmitting) return;

            // Validate message length (must be at least 20 characters)
            const message = this.formData?.additionalFeedback?.trim() || '';
            if (!message) {
                this.$alertStore?.pushError(
                    'Please provide feedback message'
                );
                return;
            }

            this.isSubmitting = true;

            // Helper: Validate rating is between 1 and 5
            const isValidRating = (rating) =>
                rating === null || (!isNaN(rating) && rating >= 1 && rating <= 5);

            // Build features_used array
            const featuresUsed = this.formData.featuresUsed
                .filter((featureValue) => featureValue != null)
                .map((featureValue) => ({
                    feature: this.featureValueToLabel[featureValue] || featureValue,
                }));

            // Build feature_ratings array
            const featureRatings = Object.entries(this.formData.satisfaction)
                .filter(([, rating]) => rating != null)
                .map(([featureValue, rating]) => ({
                    feature: this.featureValueToLabel[featureValue] || featureValue,
                    rating: parseInt(rating, 10),
                }))
                .filter(({ rating }) => isValidRating(rating));

            // Parse and validate recommendation rating
            const recommendationRating = this.formData?.recommendation != null
                ? parseInt(this.formData.recommendation, 10)
                : null;

            if (!isValidRating(recommendationRating)) {
                this.$alertStore?.pushError(
                    'Recommendation rating must be between 1 and 5.'
                );
                this.isSubmitting = false;
                return;
            }

            const feedbackData = {
                message: message,
                recommendation_rating: recommendationRating,
                allow_contact: this.formData.mayContact,
                features_used: JSON.stringify(featuresUsed),
                feature_ratings: JSON.stringify(featureRatings),
            };

            // Optimistic UI: Close modal immediately, then process in background
            this.$alertStore?.pushSuccess('Thank you for your feedback!');
            this.$emit('submit', this.formData);
            
            // Mark as completed for this period (adds 3 month delay)
            const userId = this.$userStore?.user?.id;
            if (userId) {
                setLastShownDate(userId);
            }

            this.isVisible = false;
            this.$emit('close');

            try {
                // Background submission
                const response = await this.$postData(
                    '/user-feedback/user-experience',
                    feedbackData
                );

                if (!response?.success) {
                    // Start silent error logging or maybe a toast if it failed badly?
                    // For now, since we already told them success, we might suppress details 
                    // or show a generic "We had trouble saving your feedback but we heard you" message if critical.
                    // Given the lower stakes of feedback, silent failure or console log is often acceptable 
                    // vs confusing the user after saying "Success".
                    console.warn('Feedback background submission reported failure:', response?.message);
                }
            } catch (error) {
                // Background failure
                console.error('Error submitting feedback in background:', error);
                // Since we already closed the modal and showed success, we avoid disturbing the user flow 
                // unless critical.
            } finally {
                this.isSubmitting = false;
            }
        },
        skipFeedback() {
             const userId = this.$userStore?.user?.id;
            if (userId) {
                // Snooze for 30 days
                setFeedbackSnooze(userId, 30);
            }
            
            this.$emit('skip');
            this.resetForm();
            this.isVisible = false;
            this.$emit('close');
        },
        resetForm() {
            this.formData = {
                featuresUsed: [],
                satisfaction: {
                    'auditing-suite': null,
                    'education-tracking': null,
                    'knowledge-library': null,
                    'ask-patty': null,
                },
                recommendation: null,
                additionalFeedback: '',
                mayContact: false,
            };
            // Reset image error state and submission state when form is reset
            this.imageLoadError = false;
            this.isSubmitting = false;
        },
    },
    watch: {
        isVisible(newValue) {
            if (typeof document === 'undefined') return;

            if (newValue) {
                // Store previous overflow value before changing it
                this.previousBodyOverflow = document.body.style.overflow || '';
                // Prevent body scroll when modal is open
                document.body.style.overflow = 'hidden';
            } else {
                // Reset form when modal is closed
                this.resetForm();
                // Only restore body scroll if we were the one who changed it
                if (this.previousBodyOverflow !== null) {
                    document.body.style.overflow = this.previousBodyOverflow;
                    this.previousBodyOverflow = null;
                }
            }
        },
        // Watch for when user becomes available and authenticated
        '$userStore.user': {
            handler() {
                this.checkAndShowFeedbackModal();
            },
            
        },
        '$userStore.isAuthenticated': {
            handler() {
                this.checkAndShowFeedbackModal();
            },
            immediate: true,
        },
    },
    mounted() {
        if (typeof document === 'undefined') return;

        if (this.isVisible) {
            this.previousBodyOverflow = document.body.style.overflow || '';
            document.body.style.overflow = 'hidden';
        }

        // Check on mount after a brief delay to ensure user data is loaded
        this.$nextTick(() => {
            setTimeout(() => {
                this.checkAndShowFeedbackModal();
            }, 1000);
        });
    },
    beforeUnmount() {
        // Restore body scroll when component is destroyed
        if (typeof document === 'undefined') return;

        if (this.previousBodyOverflow !== null) {
            document.body.style.overflow = this.previousBodyOverflow;
        } else {
            // Fallback: only restore if no other modal might be managing it
            // Check if body still has overflow hidden (might be from another modal)
            if (document.body.style.overflow === 'hidden') {
                document.body.style.overflow = '';
            }
        }
    },
};
</script>

<style scoped>
/* Overlay */
.feedback-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(64, 89, 81, 0.25);
    z-index: 1059;
}

/* Modal transition styles */
.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
    opacity: 0;
    transform: translateY(20px);
}

/* Feedback Modal */
.feedback-modal {
    background-color: white;
    z-index: 1062;
    width: 500px;
    max-width: 95%;
    height: fit-content;
    max-height: calc(100vh - 2rem);
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: var(--radius-lg);
    padding: var(--space-6);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Close button */
.feedback-close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
}

/* Add padding to viewport to ensure spacing */
@media (min-height: 600px) {
    .feedback-modal {
        max-height: calc(100vh - 3rem);
    }
}

.feedback-modal-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
}

.feedback-icon {
    width: 60px;
    height: 60px;
    flex-shrink: 0;
}

.feedback-title {
    font-size: 1.375rem;
    font-weight: 700;
    color: var(--neutral-900);
    text-align: center;
    line-height: var(--space-7);
    flex-shrink: 0;
}

/* Scrollable form area */
.feedback-form-scrollable {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    min-height: 0;
    padding-right: 0.5rem;
}

/* Scrollbar styling */
.feedback-form-scrollable::-webkit-scrollbar {
    width: 3px;
    opacity: 0;
    transition: all 0.3s ease;
}

.feedback-form-scrollable:hover::-webkit-scrollbar {
    opacity: 1;
}

.feedback-form-scrollable::-webkit-scrollbar-track {
    background: var(--neutral-450);
    border-radius: var(--radius-sm);
}

.feedback-form-scrollable::-webkit-scrollbar-thumb {
    background: var(--neutral-600);
    border-radius: var(--radius-sm);
}

.feedback-form-scrollable::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-700);
}

.feedback-form {
    width: 100%;
    text-align: left;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

.form-section {
    display: flex;
    flex-direction: column;
}

.form-section-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--neutral-900);
    line-height: 1.4;
}

.satisfaction-scale-label {
    font-size: 0.875rem;
    font-weight: 400;
    font-style: italic;
    color: var(--neutral-800);
    line-height: 1.4;
    margin-left: 0;
}

.satisfaction-item {
    display: flex;
    flex-direction: column;
}

.satisfaction-row {
    display: flex;
    align-items: center;
    gap: var(--space-4);
}

.satisfaction-feature-label {
    font-size: 0.875rem;
    font-weight: 400;
    color: var(--neutral-800);
    margin: 0;
    min-width: 150px;
    flex-shrink: 0;
}

.radio-group-wrapper {
    display: flex;
    align-items: center;
    flex: 1;
}

.radio-group-wrapper :deep(.radio-group) {
    display: flex;
    gap: var(--space-4);
    flex-wrap: nowrap;
    align-items: center;
}

.radio-group-wrapper :deep(.form-check) {
    margin: 0;
    padding: 0;
    display: flex;
    align-items: center;
    gap: var(--space-2);
}

.radio-group-wrapper :deep(.form-check-label) {
    margin: 0;
    display: flex;
    align-items: center;
}

.feedback-actions {
    margin-top: var(--space-2);
    padding-top: var(--space-4);
    width: 100%;
    display: flex;
    gap: var(--space-2);
    flex-shrink: 0;
}

@media (max-width: 767.98px) {
    .feedback-modal {
        width: 320px;
        max-height: calc(100vh - 2rem);
        padding: var(--space-4);
    }

    .feedback-icon {
        width: 36px;
        height: 36px;
    }

    .feedback-title {
        font-size: 1.125rem;
    }

    .satisfaction-row {
        flex-direction: column;
        align-items: flex-start;
        gap: var(--space-2);
    }

    .satisfaction-feature-label {
        min-width: auto;
    }

    .radio-group-wrapper {
        width: 100%;
    }

    .feedback-actions {
        flex-direction: column;
    }

    .feedback-actions .btn {
        width: 100%;
    }
}
</style>
